import React, { useState, useEffect, useCallback } from 'react';
import Pagination from './Pagination';
import './LoginLogs.css';

const API_BASE = '/api';

const TYPE_LABELS = {
  login: '🔑 Giriş',
  verify: '✅ Doğrulama',
  logout: '🚪 Çıkış',
  captcha: '🖼️ Captcha',
  betVerify: '🎰 Kupon Doğrulama',
  tokenRefresh: '🔄 Token Yenileme',
};

const TRIGGER_LABELS = {
  user: 'Kullanıcı',
  system: 'Sistem',
  autoLogin: '🤖 Oto Login',
  autoLoginUI: '🤖 Oto Login (UI)',
};

function LoginLogs() {
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [users, setUsers] = useState([]);
  const [displayNameMap, setDisplayNameMap] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Default dates: current month 1st → today
  const getDefaultDates = () => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth()+1).padStart(2,'0'), d = String(now.getDate()).padStart(2,'0');
    return { start: `${y}-${m}-01`, end: `${y}-${m}-${d}` };
  };
  const defaults = getDefaultDates();

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterSuccess, setFilterSuccess] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(defaults.start);
  const [filterEndDate, setFilterEndDate] = useState(defaults.end);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const token = localStorage.getItem('token');

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/logs/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setUsers(data.users || []);
        setDisplayNameMap(data.displayNameMap || {});
      }
    } catch (err) {
      console.error('Log users fetch error:', err);
    }
  }, [token]);

  const fetchLogs = useCallback(async (page = 1, useFilters = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', 50);
      if (selectedUsers.length > 0) params.set('nesineUser', selectedUsers.join(','));
      if (useFilters) {
        if (filterType) params.set('type', filterType);
        if (filterSuccess !== '') params.set('success', filterSuccess);
        if (filterTrigger) params.set('trigger', filterTrigger);
        if (filterStartDate) params.set('startDate', filterStartDate);
        if (filterEndDate) params.set('endDate', filterEndDate);
      }

      const res = await fetch(`${API_BASE}/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLogs(data.data.logs);
        setPagination(data.data.pagination);
      }
    } catch (err) {
      console.error('Logs fetch error:', err);
    }
    setLoading(false);
  }, [token, selectedUsers, filterType, filterSuccess, filterTrigger, filterStartDate, filterEndDate]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/logs/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
    fetchLogs(1, false);
    fetchStats();
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchLogs(1, filtersApplied);
  }, [selectedUsers]); // eslint-disable-line

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    fetchLogs(newPage, filtersApplied);
    setExpandedRows(new Set());
  };

  const handleFilter = () => {
    setFiltersApplied(true);
    fetchLogs(1, true);
    setExpandedRows(new Set());
  };

  const handleClearFilters = () => {
    setFilterType('');
    setFilterSuccess('');
    setFilterTrigger('');
    setFilterStartDate(defaults.start);
    setFilterEndDate(defaults.end);
    setFiltersApplied(false);
    fetchLogs(1, false);
    setExpandedRows(new Set());
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '-';
    const date = new Date(isoStr);
    return date.toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="login-logs">
      {/* Header */}
      <div className="logs-header">
        <div className="logs-header-left">
          <h2>📋 Login Logları</h2>
          {stats && (
            <div className="logs-stats">
              <span className="stat-item stat-total">{stats.total} toplam</span>
              <span className="stat-item stat-login-ok">🔑 {stats.loginSuccess} giriş</span>
              <span className="stat-item stat-login-fail">❌ {stats.loginFail} başarısız</span>
              <span className="stat-item stat-verify-ok">✅ {stats.verifySuccess} doğrulama</span>
              <span className="stat-item stat-verify-fail">⚠️ {stats.verifyFail} d. başarısız</span>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="logs-filters">
        <div className="filter-group">
          <label>Tip</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">Tümü</option>
            <option value="login">Giriş</option>
            <option value="verify">Doğrulama</option>
            <option value="betVerify">Kupon Doğrulama</option>
            <option value="logout">Çıkış</option>
            <option value="captcha">Captcha</option>
            <option value="tokenRefresh">Token Yenileme</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Durum</label>
          <select value={filterSuccess} onChange={e => setFilterSuccess(e.target.value)}>
            <option value="">Tümü</option>
            <option value="true">Başarılı</option>
            <option value="false">Başarısız</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Tetikleyen</label>
          <select value={filterTrigger} onChange={e => setFilterTrigger(e.target.value)}>
            <option value="">Tümü</option>
            <option value="user">Kullanıcı</option>
            <option value="system">Sistem</option>            <option value="autoLogin">Oto Login</option>
            <option value="autoLoginUI">Oto Login (UI)</option>          </select>
        </div>
        <div className="filter-group">
          <label>Başlangıç</label>
          <input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} />
        </div>
        <div className="filter-group">
          <label>Bitiş</label>
          <input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} />
        </div>
        <div className="filter-actions">
          <button className="filter-btn" onClick={handleFilter}>Filtrele</button>
          <button className="filter-clear-btn" onClick={handleClearFilters}>Temizle</button>
        </div>
      </div>

      {/* User toggle buttons */}
      {users.length > 1 && (
        <div className="ca-user-btns">
          <button className={`ca-user-btn ${selectedUsers.length === 0 ? 'active' : ''}`} onClick={() => setSelectedUsers([])}>
            Tümü
          </button>
          {users.map(u => (
            <button key={u} className={`ca-user-btn ${selectedUsers.includes(u) ? 'active' : ''}`} onClick={() => {
              setSelectedUsers(prev => prev.includes(u) ? prev.filter(x => x !== u) : [...prev, u]);
            }}>
              {displayNameMap[u] || u}
            </button>
          ))}
        </div>
      )}

      {/* Top Pagination */}
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={(p) => { handlePageChange(p); }}
      />

      {/* Table */}
      <div className="logs-table-container">
        {loading ? (
          <div className="logs-loading">
            <div className="spinner" />
            <p>Yükleniyor...</p>
          </div>
        ) : (
          <table className="logs-table">
            <thead>
              <tr>
                <th className="th-log-time">Zaman</th>
                <th className="th-log-user">Kullanıcı</th>
                <th className="th-log-type">Tip</th>
                <th className="th-log-status">Durum</th>
                <th className="th-log-trigger">Tetikleyen</th>
                <th className="th-log-error">Hata / Detay</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-logs">
                    <div className="no-logs-content">
                      <span className="no-logs-icon">📭</span>
                      <p>Henüz log kaydı yok.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map(log => {
                  const isExpanded = expandedRows.has(log._id);
                  return (
                    <React.Fragment key={log._id}>
                      <tr
                        className={`log-row log-row-clickable ${log.success ? '' : 'log-row-fail'}`}
                        onClick={() => toggleRow(log._id)}
                      >
                        <td className="td-log-time">{formatTime(log.createdAt)}</td>
                        <td className="td-log-user">
                          <span className="log-user-badge">{displayNameMap[log.nesineUser] || log.nesineUser || '-'}</span>
                        </td>
                        <td className="td-log-type">
                          <span className={`log-type-badge log-type-${log.type}`}>
                            {TYPE_LABELS[log.type] || log.type}
                          </span>
                        </td>
                        <td className="td-log-status">
                          <span className={`log-status-badge ${log.success ? 'log-status-ok' : 'log-status-fail'}`}>
                            {log.success ? '✅ Başarılı' : '❌ Başarısız'}
                          </span>
                        </td>
                        <td className="td-log-trigger">
                          <span className={`log-trigger-badge log-trigger-${log.trigger}`}>
                            {TRIGGER_LABELS[log.trigger] || log.trigger}
                          </span>
                        </td>
                        <td className="td-log-error">
                          {log.error ? (
                            <span className="log-error-text">{log.error}</span>
                          ) : log.meta?.source ? (
                            <span className="log-meta-text">{log.meta.source}</span>
                          ) : (
                            <span className="log-no-error">-</span>
                          )}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="log-detail-row">
                          <td colSpan="6">
                            <div className={`log-detail-panel ${log.success ? 'log-panel-success' : 'log-panel-fail'}`}>
                              <div className="log-detail-sections">
                                {log.captchaImage && (
                                  <div className="log-section">
                                    <h4>Captcha Görseli {log.request?.captchaText ? `→ "${log.request.captchaText}"` : ''}</h4>
                                    <img
                                      src={`data:image/png;base64,${log.captchaImage}`}
                                      alt="Captcha"
                                      style={{ background: '#fff', borderRadius: 4, width: 400, height: 160, imageRendering: 'pixelated', display: 'block', marginBottom: 8 }}
                                    />
                                  </div>
                                )}
                                {log.request && (
                                  <div className="log-section">
                                    <h4>Request</h4>
                                    <pre>{JSON.stringify(log.request, null, 2)}</pre>
                                  </div>
                                )}
                                {log.response && (
                                  <div className="log-section">
                                    <h4>Response</h4>
                                    <pre>{JSON.stringify(log.response, null, 2)}</pre>
                                  </div>
                                )}
                                {log.meta && (
                                  <div className="log-section">
                                    <h4>Meta</h4>
                                    <pre>{JSON.stringify(log.meta, null, 2)}</pre>
                                  </div>
                                )}
                                {!log.request && !log.response && !log.meta && (
                                  <div className="log-section">
                                    <h4>Detay</h4>
                                    <pre>Detay bilgisi yok.</pre>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Bottom Pagination */}
      <Pagination
        page={pagination.page}
        totalPages={pagination.totalPages}
        total={pagination.total}
        onPageChange={(p) => { handlePageChange(p); }}
      />
    </div>
  );
}

export default LoginLogs;
